(() => {
  if (customElements.get('sino-banner-art')) return;
  const smooth = t => t * t * (3 - 2 * t);
  const rounded = (x,y,w,h,r) => `M${x+r} ${y}H${x+w-r}Q${x+w} ${y} ${x+w} ${y+r}V${y+h-r}Q${x+w} ${y+h} ${x+w-r} ${y+h}H${x+r}Q${x} ${y+h} ${x} ${y+h-r}V${y+r}Q${x} ${y} ${x+r} ${y}Z`;
  class SinoBannerArt extends HTMLElement {
    connectedCallback() {
      this.button = this.closest('a.sino-banner-button');
      if (!this.button || this.controller) return;
      if (!this.querySelector('.sc-cart-gold-label') || !this.button.querySelector('.sino-banner-label')) {
        this.readyFrame = requestAnimationFrame(() => { if (this.isConnected) this.connectedCallback(); });
        return;
      }
      this.progress = this.target = this.frame = this.touchUntil = 0;
      this.pressed = this.held = false;
      this.animations = [];
      this.styleCache = new WeakMap();
      this.controller = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.parts = Object.fromEntries(['lines','shell','face','gold-back','gold-front','gold-shell','gold-face','gold-label'].map(k => [k,this.querySelector('.sc-cart-'+k)]));
      this.label = this.button.querySelector('.sino-banner-label');
      this.makeLayers();
      this.bodySVGs = [...this.querySelectorAll('.sc-cart-lines,.sc-cart-gold-art')];
      const rect = this.button.getBoundingClientRect();
      this.visible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      const opts = {signal:this.controller.signal};
      for (const name of ['pointerenter','pointerleave','focus','blur']) this.button.addEventListener(name,e => {
        if (name === 'pointerleave' && e.pointerType !== 'mouse') return;
        if ((name === 'pointerleave' || name === 'blur') && !this.held && performance.now() >= this.touchUntil) this.pressed = false;
        this.sync();
      },opts);
      this.button.addEventListener('pointerdown',e => {
        if (this.button.disabled) return;
        this.held = this.pressed = true;
        if (e.pointerType !== 'mouse') this.holdFeedback(480);
        this.sync();
      },opts);
      this.button.addEventListener('pointerup',e => {
        this.held = false;
        if (e.pointerType !== 'mouse') this.holdFeedback(80);
        else this.pressed = false;
        this.sync();
      },opts);
      document.addEventListener('pointerup',e => {
        if (!this.held || this.button.contains(e.target)) return;
        this.held = this.pressed = false;
        this.touchUntil = 0;
        clearTimeout(this.touchTimer);
        this.sync();
      },opts);
      this.button.addEventListener('click',() => {
        if (!this.button.disabled) { this.holdFeedback(440); this.sync(); }
      },opts);
      this.button.addEventListener('pointercancel',() => {
        this.held = this.pressed = false;
        this.touchUntil = 0;
        clearTimeout(this.touchTimer);
        this.sync();
      },opts);
      this.reduced.addEventListener('change',() => this.settle(),opts);
      document.addEventListener('visibilitychange',() => document.hidden ? this.settle() : this.sync(),opts);
      this.resize = new ResizeObserver(() => this.buildGeometry());
      this.resize.observe(this.button);
      this.intersection = new IntersectionObserver(entries => {
        const visible = entries[0].isIntersecting && entries[0].intersectionRect.width > 0 && entries[0].intersectionRect.height > 0;
        if (visible !== this.visible) { this.visible = visible; this.buildGeometry(); this.settle(); }
      });
      this.intersection.observe(this.button);
      this.buildGeometry();
      this.sync();
    }
    makeLayers() {
      this.querySelectorAll('.sc-cart-shell-soft,.sc-cart-gold-shell-sharp').forEach(n => n.remove());
      const soft = this.parts.shell.cloneNode(false);
      soft.setAttribute('class','sc-cart-shell-soft');
      soft.removeAttribute('style');
      this.parts.shell.after(soft);
      const sharp = this.parts['gold-shell'].cloneNode(false);
      sharp.setAttribute('class','sc-cart-gold-shell-sharp');
      sharp.removeAttribute('style');
      this.parts['gold-shell'].before(sharp);
      this.softShell = soft;
      this.sharpGold = sharp;
      this.button.dataset.scEnhanced = 'true';
    }
    holdFeedback(ms) {
      this.touchUntil = Math.max(this.touchUntil,performance.now()+ms);
      this.pressed = true;
      clearTimeout(this.touchTimer);
      this.touchTimer = setTimeout(() => {
        if (!this.held) this.pressed = false;
        this.sync();
      },Math.max(0,this.touchUntil-performance.now())+1);
    }
    sync() {
      if (!this.isConnected) return;
      const text = this.label?.textContent.trim() || '';
      if (this.parts['gold-label'].textContent !== text) this.parts['gold-label'].textContent = text;
      const active = this.pressed || performance.now() < this.touchUntil || (this.fine.matches && this.button.matches(':hover')) || this.button.matches(':focus-visible');
      this.animate(active ? 1 : 0);
    }
    buildGeometry() {
      const w = this.button.clientWidth,h = this.button.clientHeight;
      if (!w || !h || (w === this.width && h === this.height)) return;
      this.width = w; this.height = h;
      this.bodySVGs.forEach(svg => svg.setAttribute('viewBox',`0 0 ${w} ${h}`));
      const idle = rounded(16,15,w-32,h-30,Math.min(w-32,h-30)/2), expanded = rounded(12.7,11.7,w-25.4,h-23.4,Math.min(w-25.4,h-23.4)/2), face = rounded(20.5,19.5,w-41,h-39,Math.min(w-41,h-39)/2);
      this.parts.shell.setAttribute('d',idle); this.sharpGold.setAttribute('d',idle);
      this.softShell.setAttribute('d',expanded); this.parts['gold-shell'].setAttribute('d',expanded);
      this.parts.face.setAttribute('d',face); this.parts['gold-face'].setAttribute('d',face);
      const ry = h*.62,dy = Math.max(0,h/2-14);
      this.geometry = {ry,dy,maxRx:(w/2-14)*1.03/Math.sqrt(Math.max(.05,1-(dy/ry)**2))};
      const x = (w-25.4)/(w-32),y = (h-23.4)/(h-30);
      const sharp = p => ({transform:`scale(${1+(x-1)*p},${1+(y-1)*p})`,opacity:1-p});
      const soft = p => ({transform:`scale(${(1+(x-1)*p)/x},${(1+(y-1)*p)/y})`,opacity:.94*p});
      this.tracks = [{el:this.parts.shell,at:sharp},{el:this.softShell,at:soft},{el:this.sharpGold,at:sharp},{el:this.parts['gold-shell'],at:soft}];
      this.forward = this.keyframes(0,1); this.backward = this.keyframes(1,0);
      this.snapshot(this.progress); this.draw();
      if (this.frame) this.animate(this.target,true);
    }
    keyframes(start,goal) {
      return this.tracks.map(track => Array.from({length:13},(_,i) => ({...track.at(start+(goal-start)*smooth(i/12)),offset:i/12})));
    }
    write(el,key,value) {
      let cache = this.styleCache.get(el);
      if (!cache) { cache = {}; this.styleCache.set(el,cache); }
      const text = String(value);
      if (cache[key] !== text) { el.style[key] = text; cache[key] = text; }
    }
    snapshot(p) { this.tracks?.forEach(track => Object.entries(track.at(p)).forEach(([k,v]) => this.write(track.el,k,v))); }
    cancel() { this.animations?.forEach(a => a.cancel()); this.animations = []; }
    animate(goal,force = false) {
      if (!force && goal === this.target && (this.frame || this.progress === goal)) return;
      cancelAnimationFrame(this.frame); this.frame = 0; this.cancel(); this.target = goal;
      if (!this.visible || this.reduced.matches || document.hidden) { this.settle(); return; }
      const start = this.progress,started = performance.now(),duration = 420*Math.max(Math.abs(goal-start),.12);
      this.snapshot(start);
      this.button.classList.add('sc-cart-animating');
      const frames = start === 0 && goal === 1 ? this.forward : start === 1 && goal === 0 ? this.backward : this.keyframes(start,goal);
      this.animations = this.tracks.map((track,i) => {
        const a = track.el.animate(frames[i],{duration,easing:'linear',fill:'both'});
        a.startTime = started;
        return a;
      });
      const tick = now => {
        if (!this.isConnected) return;
        const t = Math.min(1,Math.max(0,(now-started)/duration));
        this.progress = start+(goal-start)*smooth(t); this.draw();
        if (t < 1) this.frame = requestAnimationFrame(tick);
        else { this.frame = 0; this.progress = goal; this.snapshot(goal); this.cancel(); this.button.classList.remove('sc-cart-animating'); this.draw(); }
      };
      this.frame = requestAnimationFrame(tick);
    }
    settle() {
      cancelAnimationFrame(this.frame); this.frame = 0; this.cancel(); this.progress = this.target;
      this.snapshot(this.progress); this.button.classList.remove('sc-cart-animating'); this.draw();
    }
    draw() {
      if (!this.geometry) return;
      const p = this.progress,{ry,dy,maxRx} = this.geometry,margin = 21.3*p,backRy = ry+margin;
      const backRx = (this.width/2-14+margin)*1.03/Math.sqrt(Math.max(.05,1-((dy+margin)/backRy)**2));
      this.write(this.parts['gold-back'],'clipPath',`ellipse(${backRx*p}px ${backRy}px at 50% 50%)`);
      this.write(this.parts['gold-front'],'clipPath',`ellipse(${maxRx*Math.max(0,(p-.12)/.88)}px ${ry}px at 50% 50%)`);
      this.button.dataset.scProgress = String(Math.round(p*10000)/10000);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.readyFrame);
      cancelAnimationFrame(this.frame); clearTimeout(this.touchTimer); this.cancel();
      this.controller?.abort(); this.resize?.disconnect(); this.intersection?.disconnect();
      this.controller = null;
    }
  }
  customElements.define('sino-banner-art',SinoBannerArt);
})();
